/**
 * Le catalogue d'icônes du builder — la matière de la pop-in `IconLibrary`.
 *
 * Deux niveaux, volontairement :
 *  - des GROUPES THÉMATIQUES curatés (ci-dessous), qui donnent au rédacteur des
 *    entrées lisibles en français plutôt qu'une liste alphabétique de 1700
 *    noms anglais ;
 *  - le SET LUCIDE COMPLET, servi par `loadLucideIcons()` (chargé à la demande,
 *    jamais dans le bundle initial — cf. `primitives/lucideRegistry.js`).
 *
 * Les noms restent les noms Lucide (PascalCase) : c'est ce qui est stocké dans
 * la donnée du bloc et ce que rend la primitive `LucideIcon`. Seuls les
 * intitulés sont traduits.
 */

/** Un groupe = une entrée du rail de la pop-in. `label` est ce que voit l'éditeur. */
export const ICON_GROUPS = [
  {
    key: 'people',
    label: 'Personnes et organisation',
    icons: [
      { name: 'Users', label: 'Membres' },
      { name: 'User', label: 'Personne' },
      { name: 'UsersRound', label: 'Équipe' },
      { name: 'UserCheck', label: 'Membre vérifié' },
      { name: 'UserPlus', label: 'Nouveau membre' },
      { name: 'Contact', label: 'Contact' },
      { name: 'IdCard', label: 'Carte de membre' },
      { name: 'Handshake', label: 'Partenariat' },
      { name: 'Building', label: 'Institution' },
      { name: 'Building2', label: 'Bâtiment' },
      { name: 'Landmark', label: 'Institution publique' },
      { name: 'Briefcase', label: 'Métier' },
      { name: 'Store', label: 'Commerce' },
      { name: 'Factory', label: 'Usine' },
    ],
  },
  {
    key: 'geo',
    label: 'Lieux et monde',
    icons: [
      { name: 'Globe', label: 'Monde' },
      { name: 'Map', label: 'Carte' },
      { name: 'MapPin', label: 'Adresse' },
      { name: 'MapPinned', label: 'Point de repère' },
      { name: 'Navigation', label: 'Direction' },
      { name: 'Compass', label: 'Boussole' },
      { name: 'Route', label: 'Itinéraire' },
      { name: 'Milestone', label: 'Étape' },
      { name: 'Locate', label: 'Localisation' },
      { name: 'Flag', label: 'Pays' },
    ],
  },
  {
    key: 'awards',
    label: 'Distinctions et confiance',
    icons: [
      { name: 'Star', label: 'Étoile' },
      { name: 'Award', label: 'Récompense' },
      { name: 'Trophy', label: 'Trophée' },
      { name: 'Medal', label: 'Médaille' },
      { name: 'BadgeCheck', label: 'Label' },
      { name: 'Crown', label: 'Excellence' },
      { name: 'Sparkles', label: 'Nouveauté' },
      { name: 'Gem', label: 'Valeur' },
      { name: 'Target', label: 'Objectif' },
      { name: 'Shield', label: 'Protection' },
      { name: 'ShieldCheck', label: 'Certification' },
      { name: 'ThumbsUp', label: 'Satisfaction' },
    ],
  },
  {
    key: 'data',
    label: 'Données et mesures',
    icons: [
      { name: 'TrendingUp', label: 'Croissance' },
      { name: 'TrendingDown', label: 'Baisse' },
      { name: 'ChartColumn', label: 'Statistiques' },
      { name: 'ChartLine', label: 'Courbe' },
      { name: 'ChartPie', label: 'Répartition' },
      { name: 'Activity', label: 'Activité' },
      { name: 'Percent', label: 'Pourcentage' },
      { name: 'Gauge', label: 'Indicateur' },
      { name: 'Scale', label: 'Équilibre' },
      { name: 'Calculator', label: 'Calcul' },
      { name: 'Table', label: 'Tableau' },
      { name: 'Database', label: 'Données' },
    ],
  },
  {
    key: 'finance',
    label: 'Argent et commerce',
    icons: [
      { name: 'Euro', label: 'Euro' },
      { name: 'DollarSign', label: 'Dollar' },
      { name: 'Banknote', label: 'Billet' },
      { name: 'Coins', label: 'Pièces' },
      { name: 'CreditCard', label: 'Carte bancaire' },
      { name: 'PiggyBank', label: 'Épargne' },
      { name: 'Wallet', label: 'Portefeuille' },
      { name: 'ShoppingCart', label: 'Panier' },
      { name: 'ShoppingBag', label: 'Achat' },
      { name: 'Receipt', label: 'Facture' },
      { name: 'Tag', label: 'Tarif' },
    ],
  },
  {
    key: 'comm',
    label: 'Échange et communication',
    icons: [
      { name: 'Heart', label: 'Engagement' },
      { name: 'MessageSquare', label: 'Messages' },
      { name: 'MessageCircle', label: 'Discussion' },
      { name: 'Mail', label: 'Courriel' },
      { name: 'Send', label: 'Envoi' },
      { name: 'Share2', label: 'Partage' },
      { name: 'Megaphone', label: 'Communication' },
      { name: 'Phone', label: 'Téléphone' },
      { name: 'Bell', label: 'Alerte' },
      { name: 'AtSign', label: 'Mention' },
      { name: 'Rss', label: 'Flux' },
      { name: 'Vote', label: 'Vote' },
    ],
  },
  {
    key: 'learn',
    label: 'Savoir et innovation',
    icons: [
      { name: 'GraduationCap', label: 'Formation' },
      { name: 'BookOpen', label: 'Connaissance' },
      { name: 'Book', label: 'Documentation' },
      { name: 'Lightbulb', label: 'Idée' },
      { name: 'Zap', label: 'Énergie' },
      { name: 'Brain', label: 'Expertise' },
      { name: 'Microscope', label: 'Recherche' },
      { name: 'FlaskConical', label: 'Expérimentation' },
      { name: 'Puzzle', label: 'Modularité' },
      { name: 'Rocket', label: 'Lancement' },
    ],
  },
  {
    key: 'logistics',
    label: 'Transport et logistique',
    icons: [
      { name: 'Box', label: 'Colis' },
      { name: 'Package', label: 'Emballage' },
      { name: 'Truck', label: 'Camion' },
      { name: 'Plane', label: 'Avion' },
      { name: 'Ship', label: 'Navire' },
      { name: 'TrainFront', label: 'Train' },
      { name: 'Car', label: 'Voiture' },
      { name: 'Bike', label: 'Vélo' },
      { name: 'Container', label: 'Conteneur' },
      { name: 'Warehouse', label: 'Entrepôt' },
      { name: 'Anchor', label: 'Port' },
      { name: 'Forklift', label: 'Manutention' },
    ],
  },
  {
    key: 'nature',
    label: 'Environnement et santé',
    icons: [
      { name: 'Leaf', label: 'Écologie' },
      { name: 'TreeDeciduous', label: 'Arbre' },
      { name: 'Sprout', label: 'Pousse' },
      { name: 'Recycle', label: 'Recyclage' },
      { name: 'Sun', label: 'Soleil' },
      { name: 'CloudSun', label: 'Météo' },
      { name: 'Droplet', label: 'Eau' },
      { name: 'Thermometer', label: 'Température' },
      { name: 'HeartPulse', label: 'Santé' },
      { name: 'Stethoscope', label: 'Médecine' },
      { name: 'Cross', label: 'Secours' },
      { name: 'PawPrint', label: 'Animaux' },
    ],
  },
  {
    key: 'ui',
    label: 'Interface et actions',
    icons: [
      { name: 'Check', label: 'Coche' },
      { name: 'CircleCheck', label: 'Validé' },
      { name: 'X', label: 'Fermer' },
      { name: 'Plus', label: 'Ajouter' },
      { name: 'Minus', label: 'Retirer' },
      { name: 'Search', label: 'Rechercher' },
      { name: 'Funnel', label: 'Filtrer' },
      { name: 'Settings', label: 'Réglages' },
      { name: 'Pencil', label: 'Modifier' },
      { name: 'Trash2', label: 'Supprimer' },
      { name: 'Download', label: 'Télécharger' },
      { name: 'Upload', label: 'Téléverser' },
      { name: 'Eye', label: 'Voir' },
      { name: 'Lock', label: 'Verrouillé' },
      { name: 'Calendar', label: 'Date' },
      { name: 'Clock', label: 'Durée' },
      { name: 'ArrowRight', label: 'Suivant' },
      { name: 'ArrowLeft', label: 'Précédent' },
      { name: 'ChevronRight', label: 'Dérouler' },
      { name: 'ExternalLink', label: 'Lien externe' },
      { name: 'Link', label: 'Lien' },
      { name: 'Menu', label: 'Menu' },
      { name: 'Play', label: 'Lecture' },
      { name: 'Info', label: 'Information' },
      { name: 'CircleQuestionMark', label: 'Aide' },
      { name: 'TriangleAlert', label: 'Avertissement' },
      { name: 'Layers', label: 'Niveaux' },
      { name: 'Network', label: 'Réseau' },
      { name: 'Folder', label: 'Dossier' },
      { name: 'FileText', label: 'Document' },
      { name: 'Image', label: 'Image' },
      { name: 'Video', label: 'Vidéo' },
      { name: 'Music', label: 'Musique' },
      { name: 'Wifi', label: 'Connexion' },
      { name: 'Smartphone', label: 'Mobile' },
      { name: 'Monitor', label: 'Bureau' },
    ],
  },
]

/** Intitulé français d'une icône curatée, s'il y en a un. */
const LABEL_BY_NAME = new Map()
for (const group of ICON_GROUPS) {
  for (const icon of group.icons) if (!LABEL_BY_NAME.has(icon.name)) LABEL_BY_NAME.set(icon.name, icon.label)
}
export const iconLabel = (name) => LABEL_BY_NAME.get(name) ?? null

/**
 * Amorces de recherche françaises → fragment du nom Lucide.
 *
 * Le set complet est nommé en anglais : sans cette table, chercher « flèche »
 * ou « poubelle » ne rend RIEN sur les 1700 icônes, et le rédacteur en conclut
 * qu'elles n'existent pas. La table ne couvre que les intentions courantes —
 * elle s'allonge à l'usage, elle n'a pas vocation à être exhaustive.
 */
const SEARCH_ALIASES = {
  fleche: 'arrow', utilisateur: 'user', personne: 'user', membre: 'user', equipe: 'users',
  maison: 'home', accueil: 'home', coeur: 'heart', panier: 'shopping', achat: 'shopping',
  calendrier: 'calendar', date: 'calendar', horloge: 'clock', heure: 'clock', duree: 'timer',
  fichier: 'file', document: 'file', dossier: 'folder', poubelle: 'trash', corbeille: 'trash',
  crayon: 'pencil', modifier: 'pencil', loupe: 'search', rechercher: 'search', recherche: 'search',
  etoile: 'star', telephone: 'phone', courriel: 'mail', email: 'mail', message: 'message',
  carte: 'map', monde: 'globe', terre: 'globe', reglages: 'settings', parametres: 'settings',
  verrou: 'lock', cadenas: 'lock', graphique: 'chart', statistique: 'chart', camembert: 'chart-pie',
  avion: 'plane', camion: 'truck', voiture: 'car', velo: 'bike', train: 'train', bateau: 'ship',
  boite: 'box', colis: 'package', livre: 'book', musique: 'music', video: 'video', image: 'image',
  photo: 'camera', appareil: 'camera', camera: 'camera', cloche: 'bell', alerte: 'alert',
  drapeau: 'flag', epingle: 'pin', lien: 'link', ampoule: 'lightbulb', eclair: 'zap',
  feu: 'flame', arbre: 'tree', feuille: 'leaf', soleil: 'sun', lune: 'moon', nuage: 'cloud',
  coche: 'check', valide: 'check', croix: 'x', plus: 'plus', moins: 'minus', cle: 'key',
  bouclier: 'shield', cible: 'target', trophee: 'trophy', cadeau: 'gift', euro: 'euro',
  argent: 'banknote', monnaie: 'coins', batiment: 'building', usine: 'factory', boutique: 'store',
  telecharger: 'download', televerser: 'upload', partager: 'share', imprimer: 'printer',
  oeil: 'eye', voir: 'eye', filtre: 'filter', menu: 'menu', lecture: 'play', pause: 'pause',
  information: 'info', aide: 'help', question: 'help', reseau: 'network', utilisateurs: 'users',
}

/** Sans accents, sans casse, sans tirets : la forme sur laquelle on compare. */
export const normalize = (text) =>
  (text ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\s_-]+/g, '')

/**
 * Les fragments qu'une saisie doit faire chercher : la saisie elle-même, plus
 * l'amorce anglaise de chaque mot français reconnu.
 */
export const searchTerms = (query) => {
  const cleaned = normalize(query)
  if (!cleaned) return []
  const terms = new Set([cleaned])
  for (const word of query.toLowerCase().split(/[\s,]+/).filter(Boolean)) {
    const alias = SEARCH_ALIASES[normalize(word)]
    if (alias) terms.add(normalize(alias))
  }
  return [...terms]
}

/** Une icône répond-elle à la recherche, par son nom Lucide ou son intitulé ? */
export const matchesIcon = (name, terms) => {
  if (terms.length === 0) return true
  const haystack = normalize(name) + ' ' + normalize(iconLabel(name) ?? '')
  return terms.some((term) => haystack.includes(term))
}
